import * as Sentry from '@sentry/react'
import React, { Component, ErrorInfo, ReactNode } from 'react'
import { ErrorFallback } from './ErrorFallback'

interface Props {
    children: ReactNode
    fallback?: React.ComponentType<ErrorFallbackProps>
    onError?: (error: Error, errorInfo: ErrorInfo) => void
}

interface State {
    hasError: boolean
    error: Error | null
    errorInfo: ErrorInfo | null
    errorId: string | null
}

export interface ErrorFallbackProps {
    error: Error | null
    errorInfo: ErrorInfo | null
    resetError: () => void
    errorId: string | null
}

class ErrorBoundary extends Component<Props, State> {
    /** Instance flag — synchronous guard against componentDidCatch re-entry before state propagates */
    private _handlingError = false

    constructor(props: Props) {
        super(props)
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
            errorId: null,
        }
    }

    static getDerivedStateFromError(error: Error): Partial<State> {
        // Generate a unique error ID for tracking
        const errorId = `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`

        return {
            hasError: true,
            error,
            errorId,
        }
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        // Instance variable guard prevents re-entry before React propagates the state update.
        // this.state.errorInfo is stale during rapid back-to-back calls (React batching),
        // so a plain state check is insufficient — the instance flag is synchronous.
        if (this._handlingError) return
        this._handlingError = true

        this.setState({ errorInfo })
        this.props.onError?.(error, errorInfo)
        this.logErrorToSentry(error, errorInfo)
    }

    logErrorToSentry = (error: Error, errorInfo: ErrorInfo) => {
        // Log to Sentry with additional context
        Sentry.withScope(scope => {
            // Add error boundary specific context
            scope.setTag('errorBoundary', true)
            scope.setTag('errorId', this.state.errorId)
            scope.setLevel('error')

            // Add component stack as extra context
            scope.setContext('componentStack', {
                componentStack: errorInfo.componentStack,
            })

            // Add additional error context
            scope.setContext('errorDetails', {
                errorId: this.state.errorId,
                timestamp: new Date().toISOString(),
                url: window.location.href,
                userAgent: navigator.userAgent,
            })

            // Capture the exception
            Sentry.captureException(error)
        })

        // Also log to console in development for immediate debugging
        if (import.meta.env.VITE_APP_ENV === 'development') {
            console.warn('Error logged to Sentry:', {
                error: error.message,
                stack: error.stack,
                componentStack: errorInfo.componentStack,
                timestamp: new Date().toISOString(),
                errorId: this.state.errorId,
            })
        }
    }

    resetError = () => {
        this._handlingError = false
        this.setState({
            hasError: false,
            error: null,
            errorInfo: null,
            errorId: null,
        })
    }

    render() {
        if (this.state.hasError) {
            const FallbackComponent = this.props.fallback || ErrorFallback

            return (
                <FallbackComponent
                    error={this.state.error}
                    errorInfo={this.state.errorInfo}
                    resetError={this.resetError}
                    errorId={this.state.errorId}
                />
            )
        }

        return this.props.children
    }
}

export default ErrorBoundary
